import { useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './form';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { useTranslation } from 'react-i18next';

const COUNTRIES_DATA = [
  { name: 'United States of America', code: 'US' },
  { name: 'Canada', code: 'CA' },
  { name: 'Puerto Rico', code: 'PR' },
];

interface CountryComboBoxProps {
  control: Control<FieldValues>;
  disabled?: boolean;
}

export function CountryComboBox({ control, disabled }: CountryComboBoxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={control}
      name='refund_to_country'
      render={({ field }) => (
        <FormItem>
          <FormLabel className='font-medium'>{t('Country')}</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant='outline'
                  role='combobox'
                  disabled={disabled}
                  className={`w-full justify-between border-border bg-input font-normal text-foreground ${!field.value && 'text-muted-foreground'}`}
                >
                  {field.value
                    ? COUNTRIES_DATA.filter(
                        (country) => country.code === field.value,
                      )?.map(
                        (country) => country.name + ' (' + country.code + ')',
                      )
                    : t('Select country')}
                  <ChevronsUpDown />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className='max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] p-0'>
              <Command>
                <CommandInput
                  placeholder={t('Search country...')}
                  className='h-9'
                />
                <CommandList>
                  <CommandEmpty>{t('No country found.')}</CommandEmpty>
                  <CommandGroup>
                    {COUNTRIES_DATA.map((country) => (
                      <CommandItem
                        value={country.name + ' (' + country.code + ')'}
                        key={country.code}
                        onSelect={() => {
                          field.onChange(country.code);
                          setOpen(false);
                        }}
                      >
                        {country.name + ' (' + country.code + ')'}
                        <Check
                          className={`ml-auto ${country.code === field.value ? 'opacity-100' : 'opacity-0'}`}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

